import {PowerBIEmbed} from 'powerbi-client-react'
import {models, Report} from 'powerbi-client'

import styled from '@emotion/styled'
import {FC, useEffect, useRef, useState} from 'react'
import IPowerBI from './IPowerBI'
import {
  applyUnileverFilterVisibility,
  syncUnileverFilterVisibility,
} from './powerBIFilterVisibility'

interface Props {
  edit: boolean
  data: IPowerBI
  home: boolean
  action: any
  setAction: any
  selectedFilter: any
}

interface PageFilterState {
  initialFilters: models.IFilter[]
  savedSelection: string | null
}

const StyledChart = styled.div<Props>`
  background-color: white;
  width: 100%;
  height: 100%;

  border: 1px solid #ddd;
  padding: 1rem;

  .report-style-class {
    width: 100%;
    height: 100%;
  }

  .content {
    opacity: ${(props) => (props.edit && !props.home ? 0.5 : 1)};
    pointer-events: ${(props) => (props.edit ? 'none' : 'unset')};
    width: 100%;
    height: 100%;
  }

  &:hover {
    .content {
      opacity: 1;
    }
  }
`

const PowerBI: FC<Props> = (props: Props) => {
  const [page, setPage] = useState<any>(null)
  const [counter, setCounter] = useState<number>(0)
  const [readyReportKey, setReadyReportKey] = useState<string | null>(null)
  const canViewUnileverFilters = props.data.can_view_unilever_filters === true
  const reportKey = JSON.stringify([
    props.data.report_id,
    props.data.embedUrl,
    canViewUnileverFilters,
  ])
  const reportKeyRef = useRef(reportKey)
  reportKeyRef.current = reportKey
  const intervalRef = useRef<number | null>(null)
  const mountedRef = useRef(true)
  const reportRef = useRef<Report | null>(null)
  const pageFiltersRef = useRef(new Map<string, PageFilterState>())
  const savedFilterQueueRef = useRef(Promise.resolve())
  const loadVersionRef = useRef(0)
  // The embed wrapper may retain event callbacks across renders.
  const reportLoadedHandlerRef = useRef(handleReportLoaded)
  reportLoadedHandlerRef.current = handleReportLoaded

  function isPageConnected(targetPage: any) {
    const iframe = targetPage?.report?.iframe
    return Boolean(mountedRef.current && iframe?.isConnected && iframe.contentWindow)
  }

  function getPageFilterKey(targetPage: any) {
    return JSON.stringify([props.data.report_id, props.data.embedUrl, targetPage.name])
  }

  async function handleReportLoaded(loadedReport: Report) {
    const loadVersion = ++loadVersionRef.current
    if (reportRef.current !== loadedReport) pageFiltersRef.current.clear()
    reportRef.current = loadedReport
    setPage(null)
    setReadyReportKey(null)
    if (intervalRef.current !== null) window.clearInterval(intervalRef.current)
    const isCurrentReport = () =>
      loadVersion === loadVersionRef.current &&
      reportKey === reportKeyRef.current &&
      isPageConnected({report: loadedReport})

    try {
      // Finish an in-flight saved selection before applying the latest permissions.
      await savedFilterQueueRef.current
      if (!isCurrentReport()) return
      // Filter levels are independent in Power BI: protect every existing filter card.
      await syncUnileverFilterVisibility(loadedReport, canViewUnileverFilters, isCurrentReport)
      if (!isCurrentReport()) return
      const pages = await loadedReport.getPages()
      await Promise.all(
        pages.map(async (reportPage) => {
          await syncUnileverFilterVisibility(reportPage, canViewUnileverFilters, isCurrentReport)
          if (!isCurrentReport()) return
          const visuals = await reportPage.getVisuals()
          await Promise.all(
            visuals.map((visual) =>
              syncUnileverFilterVisibility(visual, canViewUnileverFilters, isCurrentReport)
            )
          )
        })
      )
      if (!isCurrentReport()) return
      const activePage = await loadedReport.getActivePage()
      const filters = await syncUnileverFilterVisibility(
        activePage,
        canViewUnileverFilters,
        isCurrentReport
      )
      if (!filters || !isCurrentReport()) return
      const pageFilterKey = getPageFilterKey(activePage)
      if (!pageFiltersRef.current.has(pageFilterKey)) {
        pageFiltersRef.current.set(pageFilterKey, {initialFilters: filters, savedSelection: null})
      }
      setPage(activePage)
      setReadyReportKey(reportKey)
      props.setAction({type: 'update-filter', data: JSON.stringify(filters)})
      intervalRef.current = window.setInterval(() => {
        if (mountedRef.current) setCounter(Math.random())
      }, 2000)
    } catch (_) {
      // Keep the pane hidden if its filter permissions could not be applied.
    }
  }

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      loadVersionRef.current++
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (reportRef.current) void handleReportLoaded(reportRef.current)
  }, [canViewUnileverFilters])

  useEffect(() => {
    if (!isPageConnected(page)) return
    const pageFilterState = pageFiltersRef.current.get(getPageFilterKey(page))
    if (!pageFilterState) return
    let savedFilters: models.IFilter[] | null = null
    let savedSelection: string | null = null
    try {
      if (props.selectedFilter && props.selectedFilter !== 'none') {
        const parsed = JSON.parse(JSON.parse(props.selectedFilter).data)
        if (!Array.isArray(parsed)) return
        savedFilters = parsed
        savedSelection = props.selectedFilter
      }
    } catch (_) {
      return
    }

    let cancelled = false
    const loadVersion = loadVersionRef.current
    const isCurrentPage = () =>
      !cancelled &&
      loadVersion === loadVersionRef.current &&
      reportKey === reportKeyRef.current &&
      isPageConnected(page)

    savedFilterQueueRef.current = savedFilterQueueRef.current
      .then(async () => {
        // Loading, polling and permission changes must not reapply every native filter card.
        if (!isCurrentPage() || pageFilterState.savedSelection === savedSelection) return
        const filters = savedFilters
          ? getOnlyValidFilters(savedFilters, pageFilterState.initialFilters)
          : applyUnileverFilterVisibility(pageFilterState.initialFilters, canViewUnileverFilters)
        const currentFilters = await page.getFilters()
        if (!isCurrentPage()) return
        if (JSON.stringify(currentFilters) !== JSON.stringify(filters)) {
          await page.setFilters(filters)
        }
        pageFilterState.savedSelection = savedSelection
      })
      .catch(() => {})

    return () => {
      cancelled = true
    }
  }, [props.selectedFilter, page, canViewUnileverFilters])

  function getOnlyValidFilters(filters: models.IFilter[], initialFilters: models.IFilter[]) {
    const finalFilters = initialFilters.map((f: any) => {
      const savedFilter = filters.find(
        (item: any) =>
          item.target?.table === f.target?.table &&
          item.target?.column === f.target?.column &&
          item.filterType === f.filterType
      )
      if (!savedFilter) return f
      return {
        ...savedFilter,
        target: f.target,
        displaySettings: {...savedFilter.displaySettings, ...f.displaySettings},
      }
    })
    return applyUnileverFilterVisibility(finalFilters, canViewUnileverFilters)
  }

  async function checkFilters(targetPage: any) {
    const loadVersion = loadVersionRef.current
    try {
      const filters = await syncUnileverFilterVisibility(
        targetPage,
        canViewUnileverFilters,
        () =>
          loadVersion === loadVersionRef.current &&
          reportKey === reportKeyRef.current &&
          isPageConnected(targetPage)
      )
      if (!filters) return
      if (!props.action || JSON.stringify(filters) !== props.action.data) {
        props.setAction({type: 'update-filter', data: JSON.stringify(filters)})
      }
    } catch (_) {}
  }

  useEffect(() => {
    if (isPageConnected(page)) void checkFilters(page)
  }, [counter])

  return (
    <StyledChart {...props}>
      <div className='content'>
        <PowerBIEmbed
          embedConfig={{
            type: 'report',
            id: props.data.report_id,
            embedUrl: props.data.embedUrl,
            accessToken: props.data.accessToken,
            tokenType: models.TokenType.Embed,
            settings: {
              panes: {
                filters: {
                  visible: readyReportKey === reportKey,
                },
                pageNavigation: {
                  visible: false,
                },
              },
              background: models.BackgroundType.Default,
            },
          }}
          eventHandlers={
            new Map([
              [
                'loaded',
                function (_event: any, embeddedEntity: any) {
                  if (embeddedEntity) void reportLoadedHandlerRef.current(embeddedEntity as Report)
                },
              ],
              [
                'pageChanged',
                function (_event: any, embeddedEntity: any) {
                  if (embeddedEntity) void reportLoadedHandlerRef.current(embeddedEntity as Report)
                },
              ],
              ['rendered', function () {}],
              ['error', function () {}],
            ])
          }
          cssClassName={'report-style-class'}
        />
      </div>
    </StyledChart>
  )
}

export default PowerBI
